import { useEffect, useCallback } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { fetchEventTransactionDetailsAction } from "@/modules/eventRegistration/features/EventWaitlist/slices/eventTransactionsSlice";
import { UseEventTransactionsReturn } from "@/modules/eventRegistration/features/EventWaitlist/hooks/useEventTransactions/types";

interface UseEventTransactionsProps {
  eventUuid?: string;
}

export default function useEventTransactions({
  eventUuid,
}: UseEventTransactionsProps = {}): UseEventTransactionsReturn {
  const { toast } = useToast();
  const dispatch = useAppDispatch();

  const {
    loadingTransactionDetails,
    transactionDetailsError,
    transactionDetails,
    loadingPaymentRefund,
    loadingVoucherReturn,
  } = useAppSelector((state) => state.eventTransactions);

  const registrationGroups = transactionDetails?.registrationGroups || [];
  const attendees = transactionDetails?.attendees || [];

  // Fetch transaction details on mount
  useEffect(() => {
    if (eventUuid) {
      dispatch(fetchEventTransactionDetailsAction({ uuid: eventUuid }));
    }
  }, [dispatch, eventUuid]);

  // Handle errors
  useEffect(() => {
    if (transactionDetailsError) {
      toast({
        title: "Error",
        description: transactionDetailsError,
        variant: "destructive",
      });
    }
  }, [transactionDetailsError, toast]);

  const refreshData = useCallback(async () => {
    if (eventUuid) {
      dispatch(fetchEventTransactionDetailsAction({ uuid: eventUuid }));
    }
  }, [dispatch, eventUuid]);

  return {
    registrationGroups,
    attendees,
    isLoading: loadingTransactionDetails,
    isRefreshing: loadingTransactionDetails,
    isProcessing: loadingPaymentRefund || loadingVoucherReturn,
    refreshData,
  };
}
